import { useEffect, useRef, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { PageHeader } from './ui/PageHeader';
import { useAdmin } from './AdminContext';
import { siteIssues } from '../data/site';
import { SalonSettings } from './settings/SalonSettings';
import { SalonDaysSettings } from './settings/SalonDaysSettings';
import { AppointmentSettings } from './settings/AppointmentSettings';
import { StaffSettings } from './settings/StaffSettings';
import { ShippingSettings } from './settings/ShippingSettings';
import { GiftSettings } from './settings/GiftSettings';
import { CodeSettings } from './settings/CodeSettings';
import { PressSettings } from './settings/PressSettings';
import { FeatureSettings } from './settings/FeatureSettings';
import { SeoSettings } from './settings/SeoSettings';
import { IntegrationSettings } from './settings/IntegrationSettings';
import { useRedirects, type RedirectsState } from './settings/useRedirects';
type TopicKey = 'salon' | 'team' | 'salondagen' | 'afspraken' | 'webshop' | 'cadeaus' | 'vindbaarheid' | 'systeem';
type Go = (key: TopicKey) => void;
type Ctx = {
  go: Go;
  redirects: RedirectsState;
};

/** Acht onderwerpen, één tegelijk. De volgorde volgt hoe vaak Sarah er komt. */
const topics: {
  key: TopicKey;
  label: string;
  description: string;
  render: (ctx: Ctx) => ReactElement;
}[] = [{
  key: 'salon',
  label: 'Salon & contact',
  description: 'Jij, de salon, contact en btw.',
  render: () => <SalonSettings data-id="element-3940" data-mp="b1zn0cwseq92" />
}, {
  key: 'team',
  label: 'Team',
  description: 'Wie er behandelt, en wie mag inloggen.',
  render: ({
    go
  }) => <StaffSettings onOpenProfile={() => go('salon')} data-id="element-3941" data-mp="g0ksaqqwc04d" />
}, {
  key: 'salondagen',
  label: 'Salondagen',
  description: 'De vaste week en afwijkende dagen.',
  render: () => <SalonDaysSettings data-id="element-3942" data-mp="1rh17qk2gagr8" />
}, {
  key: 'afspraken',
  label: 'Afspraken & kuren',
  description: 'Verzetten, kuren en het online consult.',
  render: () => <AppointmentSettings data-id="element-3943" data-mp="1wekp3sug39ne" />
}, {
  key: 'webshop',
  label: 'Webshop & verzending',
  description: 'Verzending, ruilen en het abonnement.',
  render: () => <ShippingSettings data-id="element-3944" data-mp="11sne5rcgdp0o" />
}, {
  key: 'cadeaus',
  label: 'Cadeaus & codes',
  description: 'Cadeaus bij bestelling, cadeaubonnen en klantcodes.',
  render: ({
    go
  }) => <>
        <GiftSettings onOpenShipping={() => go('webshop')} data-id="element-3945" data-mp="1gnjuysybvkpd" />
        <CodeSettings data-id="element-3946" data-mp="1bs4gbyshxkrw" />
      </>
}, {
  key: 'vindbaarheid',
  label: 'Vindbaarheid & pers',
  description: 'Sitemap, doorverwijzingen en pers.',
  render: ({
    redirects
  }) => <>
        <SeoSettings redirects={redirects} data-id="element-3947" data-mp="j3p7jtzyjyp0" />
        <PressSettings data-id="element-3948" data-mp="xqs49yggi3ba" />
      </>
}, {
  key: 'systeem',
  label: 'Koppelingen & privacy',
  description: 'Externe bronnen en wat er op de site aan staat.',
  render: () => <>
        <IntegrationSettings data-id="element-3949" data-mp="5ahfy2bvn37b" />
        <FeatureSettings data-id="element-3950" data-mp="59pkqsm6z7hk" />
      </>
}];

/** Onbekend of leeg `?tab=`: het eerste onderwerp. */
const topicOf = (raw: string): TopicKey => topics.find(t => t.key === raw)?.key ?? 'salon';

/**
 * De feiten van de zaak: één plek, en overal op de site klopt het. Links de onderwerpen, rechts
 * één onderwerp tegelijk. Alles hier gaat mee met de Opslaan-balk van het beheer, behalve wat er
 * zelf bij zegt dat het meteen gebeurt. Elk onderwerp staat in admin/settings/.
 */
export function Settings() {
  const {
    site,
    index
  } = useAdmin();
  const [params, setParams] = useSearchParams();
  const active = topicOf(params.get('tab') ?? '');
  const topic = topics.find(t => t.key === active) ?? topics[0];
  const issues = siteIssues(site, id => Boolean(index.productById(id)));
  // Op paginaniveau: wisselen van onderwerp gooit niet-opgeslagen doorverwijzingen niet weg.
  const redirects = useRedirects();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const switched = useRef(false);
  const go: Go = key => {
    switched.current = true;
    setParams({
      tab: key
    }, {
      replace: true
    });
  };

  // Na wisselen: focus op de kop van het onderwerp (schermlezers), en terug naar boven.
  useEffect(() => {
    if (!switched.current) return;
    switched.current = false;
    headingRef.current?.focus({
      preventScroll: true
    });
    headingRef.current?.scrollIntoView({
      block: 'start'
    });
  }, [active]);
  return <div data-id="element-3951" data-mp="1ksuaw72qilad">
      <PageHeader title="Instellingen" description="De feiten van je zaak. Pas je hier iets aan, dan klopt het overal: op de site, in de FAQ, de voorwaarden, je mails en in Google." data-id="element-3952" data-mp="1kqhvo1od2p10" />

      {issues.length > 0 && <div role="status" className="mb-6 rounded-lg border border-clay/30 bg-blush/40 px-5 py-4" data-id="element-3953" data-mp="28hnbiguaxhbb">
          <p className="flex items-center gap-2 text-[14px] text-ink" data-id="element-3954" data-mp="23iiytg8651g8">
            <AlertCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-3955" data-mp="1zhgzz0i5yres" />
            Dit klopt nog niet
          </p>
          <ul className="mt-2 list-disc space-y-0.5 pl-10 text-[13px] text-muted" data-id="element-3956" data-mp="s4ye72d60tdl">
            {issues.map(i => <li key={i} data-id="element-3957" data-mp="o0qpv6amdffw">{i}</li>)}
          </ul>
        </div>}

      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-10" data-id="element-3958" data-mp="2zqouie1qu338">
        {/* Telefoon: een keuzelijst; desktop: het zijmenu. */}
        <label className="block lg:hidden" data-id="element-3959" data-mp="33ow6ywp6ci8m">
          <span className="sr-only" data-id="element-3960" data-mp="38paiiopziabe">Onderwerp</span>
          <select value={active} onChange={e => go(topicOf(e.target.value))} className="h-11 w-full rounded-md border border-line bg-white px-3 text-[15px] text-ink" data-id="element-3961" data-mp="38oim5bokc8k2">
            {topics.map(t => <option key={t.key} value={t.key} data-id="element-3962" data-mp="3ne6q6ko4plbh">
                {t.label}
              </option>)}
          </select>
        </label>

        <nav aria-label="Onderwerpen" className="hidden lg:block" data-id="element-3963" data-mp="3rf0c58i0gq1q">
          <ul className="sticky top-20 space-y-0.5" data-id="element-3964" data-mp="a090vjrm2tl">
            {topics.map(t => {
            const current = t.key === active;
            return <li key={t.key} data-id="element-3965" data-mp="221dd5idws6ol">
                  <button type="button" onClick={() => go(t.key)} aria-current={current ? 'page' : undefined} className={`w-full rounded-md px-3 py-2 text-left text-[14px] transition-colors duration-150 ease-soft ${current ? 'bg-white text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:bg-white/60 hover:text-ink'}`} data-id="element-3966" data-mp="222oihmn05mht">
                    {t.label}
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <section aria-labelledby="instelling-kop" className="min-w-0 max-w-3xl" data-id="element-3967" data-mp="3jc29g28nop7u">
          <h2 id="instelling-kop" ref={headingRef} tabIndex={-1} className="scroll-mt-24 font-display text-[1.6rem] font-light text-ink outline-none" data-id="element-3968" data-mp="xj7jkn7zb5s">
            {topic.label}
          </h2>
          <p className="mb-6 mt-1 text-[14px] text-muted" data-id="element-3969" data-mp="avz3c0x6z8lm">{topic.description}</p>
          <div key={topic.key} className="space-y-6" data-id="element-3970" data-mp="5xxfl2otml3t">
            {topic.render({
            go,
            redirects
          })}
          </div>
        </section>
      </div>
    </div>;
}